import { useCallback, useEffect, useRef } from "react";
import { createPoller, type CreatePollerOptions, type Poller } from "@/lib/polling";

/**
 * Runs several independent pollers (keyed by caller-chosen id) per hook
 * instance. Stopping one id can never affect another — each poller is its
 * own closure with no shared mutable state.
 */
export function usePollerRegistry() {
  const pollers = useRef(new Map<string, Poller>());

  const run = useCallback(<T,>(id: string, opts: CreatePollerOptions<T>): Poller => {
    pollers.current.get(id)?.stop();
    const poller = createPoller<T>({
      ...opts,
      onDone: (data) => {
        pollers.current.delete(id);
        opts.onDone?.(data);
      },
      onTimeout: (lastData) => {
        pollers.current.delete(id);
        opts.onTimeout?.(lastData);
      },
    });
    pollers.current.set(id, poller);
    poller.start();
    return poller;
  }, []);

  const stop = useCallback((id: string) => {
    pollers.current.get(id)?.stop();
    pollers.current.delete(id);
  }, []);

  const stopAll = useCallback(() => {
    pollers.current.forEach((p) => p.stop());
    pollers.current.clear();
  }, []);

  const isActive = useCallback((id: string) => pollers.current.get(id)?.isActive() ?? false, []);

  useEffect(() => stopAll, [stopAll]);

  return { run, stop, stopAll, isActive };
}
